import { useState } from "react";
import { Lock } from "lucide-react";
import { useAwards, useSkillState, useSkills } from "../lib/api";
import { fmtXp, levelProgress, xpForLevel } from "../lib/xp";
import type { Skill, SkillState } from "../lib/types";
import { Chip, Empty, Sheet, fmtDate, fmtTime } from "../components/ui";
import { Unlocks } from "../components/Unlocks";
import { describeEvent } from "../lib/describe";

export function Skills() {
  const skills = useSkills();
  const state = useSkillState();
  const [open, setOpen] = useState<Skill | null>(null);
  const byId = new Map((state.data ?? []).map((s) => [s.skill_id, s]));
  const total = (state.data ?? []).filter((s) => s.unlocked).reduce((n, s) => n + s.level, 0);
  const totalXp = (state.data ?? []).reduce((n, s) => n + s.xp, 0);

  return (
    <div className="grid gap-4">
      <header className="flex items-end justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Skills</h1>
        <div className="text-right">
          <p className="text-xs text-ink-mute">Total level</p>
          <p className="numeral text-xl text-accent">{total}</p>
        </div>
      </header>
      <p className="-mt-2 text-sm text-ink-mute">
        <span className="numeral">{fmtXp(totalXp)}</span> xp overall
      </p>
      {skills.isLoading && <Empty>Loading…</Empty>}
      <section className="grid grid-cols-2 gap-3">
        {skills.data?.map((s) => (
          <SkillTile key={s.id} skill={s} state={byId.get(s.id)} onOpen={() => setOpen(s)} />
        ))}
      </section>
      <Unlocks />
      <SkillSheet
        skill={open}
        state={open ? byId.get(open.id) : undefined}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}

function SkillTile({
  skill,
  state,
  onOpen,
}: {
  skill: Skill;
  state?: SkillState;
  onOpen: () => void;
}) {
  const locked = !state?.unlocked;
  const xp = state?.xp ?? skill.starting_xp;
  const prog = levelProgress(xp);
  const tone = skill.id === "recovery" || skill.id === "mobility" ? "recovery" : "accent";
  return (
    <button
      onClick={onOpen}
      className={`tap card p-4 text-left transition active:scale-[0.98] ${locked ? "opacity-55" : ""}`}
    >
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink-soft">{skill.name}</span>
        {locked ? (
          <Lock size={16} className="text-ink-mute" />
        ) : (
          <span
            className={`numeral text-2xl ${tone === "recovery" ? "text-recovery" : "text-accent"}`}
          >
            {state?.level ?? 1}
          </span>
        )}
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-2">
        {!locked && (
          <div
            className={`h-full rounded-full ${tone === "recovery" ? "bg-recovery" : "bg-accent"}`}
            style={{ width: `${Math.max(2, prog.pct * 100)}%` }}
          />
        )}
      </div>
    </button>
  );
}

function SkillSheet({
  skill,
  state,
  onClose,
}: {
  skill: Skill | null;
  state?: SkillState;
  onClose: () => void;
}) {
  const awards = useAwards(skill?.id ?? "");
  if (!skill) return null;
  const locked = !state?.unlocked;
  const xp = state?.xp ?? 0;
  const prog = levelProgress(xp);
  return (
    <Sheet open onClose={onClose} title={skill.name}>
      <div className="grid gap-4">
        <p className="text-sm text-ink-soft">{skill.blurb}</p>
        {locked ? (
          <Chip>locked</Chip>
        ) : (
          <>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-ink-mute">Level</p>
                <p className="numeral text-4xl text-accent">{prog.level}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-mute">XP</p>
                <p className="numeral text-lg">{fmtXp(xp)}</p>
                {prog.level < 99 && (
                  <p className="text-xs text-ink-mute">
                    <span className="numeral">{fmtXp(xpForLevel(prog.level + 1) - xp)}</span> to{" "}
                    {prog.level + 1}
                  </p>
                )}
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${Math.max(2, prog.pct * 100)}%` }}
              />
            </div>
            <div>
              <p className="mb-2 text-xs uppercase tracking-widest text-ink-mute">
                Where it came from
              </p>
              {(awards.data ?? []).length === 0 ? (
                <Empty>Nothing yet. Log something.</Empty>
              ) : (
                <ul className="card divide-y divide-border">
                  {(awards.data ?? []).slice(0, 40).map((a) => (
                    <li key={a.event_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="truncate">
                          {a.events ? describeEvent(a.events.type, a.events.payload) : "—"}
                        </p>
                        <p className="text-xs text-ink-mute">
                          {fmtDate(a.local_date)}
                          {a.events ? ` · ${fmtTime(a.events.occurred_at)}` : ""}
                        </p>
                      </div>
                      <span className="numeral text-accent">+{fmtXp(a.xp)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
